import { createReducer, on } from '@ngrx/store';
import * as StationActions from './station.actions';
import { StationComponentDto, StationDto } from '@shared/dto';
import { PaginatedListQuery } from '@shared/interfaces/paginated-list-query.interface';
import { logout } from '@auth/store/auth.actions';
import { hydrateSuccess } from '@store/storage.actions';
import { State } from '@store/state.interface';

export const STATION_FEATURE_KEY = 'station';

export interface StationState extends State<StationDto> {
  components: StationComponentDto[] | null;
}

export const initialState: StationState = {
  data: null,
  detail: null,
  total: 0,
  query: null,
  components: null,
  loading: false,
  error: null,
};

export const stationReducer = createReducer(
  initialState,
  on(StationActions.readStations, (state, { query }) => ({
    ...state,
    query: query ?? state.query,
    loading: true,
    error: null,
  })),
  on(StationActions.readStationsSuccess, (state, { stations, total }) => ({ ...state, data: stations, total, loading: false })),
  on(StationActions.readStationsFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Detail (single entity): the resolver blocks activation and matches by id, so we
  // keep the previous detail during the load (no blank flash on the outgoing page).
  on(StationActions.readStation, (state) => ({ ...state, loading: true, error: null })),
  on(StationActions.readStationSuccess, (state, { station }) => ({ ...state, detail: station, loading: false })),
  on(StationActions.readStationFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Keep the open detail in sync with mutations so the form/restore-banner stay
  // correct without a refetch (the list itself is refetched by reloadAfterMutation$).
  on(StationActions.updateStationSuccess, (state, { station }) => ({
    ...state,
    detail: state.detail ? { ...state.detail, ...station } : state.detail,
  })),
  on(StationActions.deleteStationSuccess, (state, { station }) => ({ ...state, detail: station })),
  on(StationActions.restoreStationSuccess, (state, { station }) => ({ ...state, detail: station })),
  // Create/update/delete/restore change the page contents or totals, so the
  // current page is refetched (see StationEffects.reloadAfterMutation$) rather
  // than mutated in place — which would desync the paginator.
  on(StationActions.readStationComponents, (state) => ({ ...state, loading: true, error: null })),
  on(StationActions.readStationComponentsSuccess, (state, { components }) => ({ ...state, components, loading: false })),
  on(StationActions.readStationComponentsFailure, (state, { error }) => ({ ...state, loading: false, error })),
  on(hydrateSuccess, (state, { state: persisted }) =>
    (persisted as any).station ? { ...state, ...(persisted as any).station, detail: null } : state
  ),
  on(logout, () => initialState),
);
